// Keeps the /offline page loadable with zero network: caches its HTML and
// every /_next/static asset it references. Re-run on every sync so a deploy's
// new chunk names are picked up without a build-time precache manifest.
import { SHELL_CACHE } from "./db";

export function extractAssetUrls(html: string): string[] {
  const found = html.match(/\/_next\/static\/[^"'\s)\\>]+/g) ?? [];
  return [...new Set(found)];
}

export async function syncOfflineShell(): Promise<void> {
  const res = await fetch("/offline", { credentials: "same-origin", cache: "no-store" });
  if (!res.ok) return;
  const html = await res.text();
  const cache = await caches.open(SHELL_CACHE);
  await cache.put(
    "/offline",
    new Response(html, { headers: { "content-type": "text/html; charset=utf-8" } })
  );

  const assets = extractAssetUrls(html);
  const cached = new Set((await cache.keys()).map((r) => new URL(r.url).pathname));
  for (const url of assets) {
    if (cached.has(url)) continue; // hashed filenames: cached = current
    try {
      const assetRes = await fetch(url, { cache: "no-store" });
      if (assetRes.ok) await cache.put(url, assetRes);
    } catch {
      // Missed asset: the next sync retries. The old shell keeps working.
    }
  }

  await warmBootOfflinePage();
}

/**
 * Boot /offline for real in a hidden iframe. The HTML's <script src> list is
 * not the whole story — async-loaded chunks are requested only at runtime, and
 * hydration dies offline on the first missing one (ChunkLoadError, observed in
 * verification). Executing the page routes every chunk request through the
 * service worker, whose /_next/static handler runtime-caches each response.
 */
function warmBootOfflinePage(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.setAttribute("aria-hidden", "true");
    iframe.src = "/offline";
    const done = () => {
      iframe.remove();
      resolve();
    };
    // load fires on document load; give hydration a beat to pull async chunks.
    iframe.addEventListener("load", () => setTimeout(done, 3000));
    setTimeout(done, 15000); // hard stop if load never fires
    document.body.appendChild(iframe);
  });
}
